import { Suspense, lazy, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  queryString,
  type FamiliesResult,
  type FamilyDetailResult,
  type SkuDetail,
  type SkuListResult,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { Alert, Button, Card, Input, PageHeader, Select, Spinner } from '../components/ui.js';
import {
  CHART_LABELS,
  CHART_TYPES,
  GRAINS,
  RANGE_PRESET_LABELS,
  decodeChartState,
  encodeChartState,
  resolveRange,
  type ChartState,
  type Grain,
  type RangePreset,
} from '../lib/charts.js';
import { productPagePath } from '../lib/products.js';

/**
 * The charts explorer. Every control lives in the URL (chart type, scope, grain,
 * range), so a view is bookmarkable and shareable. The chart itself and the
 * charting library are lazy-loaded, so this page — and the app's initial bundle —
 * carry the controls but not Recharts until a chart is shown. See docs/charts.md.
 */

const ChartCanvas = lazy(() => import('../charts/ChartCanvas.js'));

/** Chart types that read a scope (account/family/SKU); top and scatter are account-wide. */
const SCOPED = new Set([
  'sales_stock',
  'sales_time',
  'yoy',
  'sessions',
  'sell_through',
  'stockout_timeline',
  'size_mix',
]);
/** Chart types drawn from the time-series endpoint (grain + range apply). */
const SERIES = new Set(['sales_stock', 'sales_time', 'sessions', 'sell_through']);
/** Chart types with a date range (series charts, top, and the timeline). The
 * sell-through scatter reads fixed windows (30/90/365 days), so it takes no range. */
const RANGED = new Set([...SERIES, 'top', 'stockout_timeline']);

export function ChartsPage() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => decodeChartState(params), [params]);
  const session = useSession();
  const timeZone = session.status === 'signedIn' ? session.businessTimeZone : undefined;

  function update(patch: Partial<ChartState>) {
    setParams(encodeChartState({ ...state, ...patch }));
  }

  // Size mix needs a family; nudge the scope when that chart is picked.
  const effectiveScope = state.type === 'size_mix' ? 'family' : state.scope;
  // On a direct URL load a size-mix view whose saved scope was not a family must
  // not reuse a SKU id (or "account") as a family key: drop it so the page prompts
  // for a family rather than firing a request with a bad key.
  const effectiveKey = state.type === 'size_mix' && state.scope !== 'family' ? null : state.key;
  // "Today" is the business day, so a preset range does not slip a day near midnight.
  const range = resolveRange(state, timeZone ? { timeZone } : {});
  const showScope = SCOPED.has(state.type);
  const showGrain = SERIES.has(state.type);
  const showRange = RANGED.has(state.type);
  const csvHref =
    SERIES.has(state.type) &&
    `/api/charts/series.csv${queryString({
      scope: effectiveScope,
      key: effectiveScope === 'account' ? undefined : (effectiveKey ?? undefined),
      grain: state.grain,
      from: range.from,
      to: range.to,
    })}`;

  const canRender =
    !showScope || effectiveScope === 'account' || (effectiveKey !== null && effectiveKey !== '');

  return (
    <div className="space-y-4">
      <PageHeader
        title="Charts"
        description="Explore sales, stock, and traffic over time. Pick a chart, a scope, a grain, and a range; the view is saved in the address, so it can be bookmarked. See docs/charts.md for what each chart shows and how buckets and weeks are defined."
      />

      <AnalysisBanner />

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Chart">
            <Select
              value={state.type}
              onChange={(e) => {
                const type = e.target.value as ChartState['type'];
                // Size mix is family-only; if the current scope is not a family,
                // clear the key so the page prompts for one rather than reusing a
                // SKU id (or account) as a family key.
                update(
                  type === 'size_mix' && state.scope !== 'family' ? { type, key: null } : { type },
                );
              }}
              aria-label="Chart type"
              className="w-56"
            >
              {CHART_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CHART_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>

          {showScope && (
            <ScopePicker
              scope={effectiveScope}
              selectedKey={effectiveKey}
              lockScope={state.type === 'size_mix'}
              // Switching scope always clears the key: a family key is never a SKU
              // id, so the chart prompts for a fresh pick and fires no request.
              onScope={(scope) => update({ scope, key: null })}
              onKey={(key) => update({ key })}
            />
          )}

          {showGrain && (
            <Field label="Grain">
              <div className="flex gap-1" role="group" aria-label="Grain">
                {GRAINS.map((g) => (
                  <Segment key={g} active={state.grain === g} onClick={() => update({ grain: g })}>
                    {GRAIN_LABEL[g]}
                  </Segment>
                ))}
              </div>
            </Field>
          )}

          {(state.type === 'sales_time' || state.type === 'yoy' || state.type === 'top') && (
            <Field label="Measure">
              <Select
                value={state.metric}
                onChange={(e) => update({ metric: e.target.value as ChartState['metric'] })}
                aria-label="Measure"
                className="w-32"
              >
                <option value="units">Units</option>
                <option value="revenue">Revenue</option>
              </Select>
            </Field>
          )}

          {state.type === 'top' && (
            <Field label="Group by">
              <Select
                value={state.by}
                onChange={(e) => update({ by: e.target.value as ChartState['by'] })}
                aria-label="Group by"
                className="w-32"
              >
                <option value="sku">SKU</option>
                <option value="family">Family</option>
              </Select>
            </Field>
          )}

          {state.type === 'sales_time' && (
            <Checkbox checked={state.compare} onChange={(v) => update({ compare: v })}>
              Compare previous year
            </Checkbox>
          )}
        </div>

        {showRange && (
          <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-line pt-3">
            <Field label="Range">
              <div className="flex flex-wrap gap-1" role="group" aria-label="Range preset">
                {(['3m', '6m', '12m', '24m', 'custom'] as RangePreset[]).map((p) => (
                  <Segment
                    key={p}
                    active={state.preset === p}
                    onClick={() => update({ preset: p })}
                  >
                    {RANGE_PRESET_LABELS[p]}
                  </Segment>
                ))}
              </div>
            </Field>
            {state.preset === 'custom' && (
              <>
                <Field label="From">
                  <Input
                    type="date"
                    value={state.from}
                    onChange={(e) => update({ from: e.target.value })}
                    aria-label="From date"
                    className="w-40"
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="date"
                    value={state.to}
                    onChange={(e) => update({ to: e.target.value })}
                    aria-label="To date"
                    className="w-40"
                  />
                </Field>
              </>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-line pt-3 text-xs">
          {/* The sell-through scatter carries its own table and CSV, so the page's
              chart/table toggle does not apply to it. */}
          {state.type !== 'scatter' && (
            <Button variant="secondary" onClick={() => update({ table: !state.table })}>
              {state.table ? 'Show as chart' : 'Show as table'}
            </Button>
          )}
          {csvHref && (
            <a
              href={csvHref}
              className="inline-flex items-center rounded-md bg-surface px-3 py-2 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
            >
              Download CSV
            </a>
          )}
        </div>
      </Card>

      <Card className="p-4">
        {!canRender ? (
          <Alert tone="info">
            Pick a {effectiveScope === 'family' ? 'family' : 'SKU'} above to draw this chart.
          </Alert>
        ) : (
          <Suspense fallback={<Spinner label="Loading chart" />}>
            <ChartCanvas
              state={{ ...state, scope: effectiveScope, key: effectiveKey }}
              range={range}
            />
          </Suspense>
        )}
      </Card>
    </div>
  );
}

const GRAIN_LABEL: Record<Grain, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted">{label}</span>
      {children}
    </label>
  );
}

function Segment({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-md px-3 py-2 text-sm font-medium ${
        active
          ? 'bg-brand-600 text-brand-on'
          : 'bg-surface text-body ring-1 ring-inset ring-line-strong hover:bg-sunken'
      }`}
    >
      {children}
    </button>
  );
}

function Checkbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 self-end py-2 text-sm text-body">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-line-strong"
      />
      {children}
    </label>
  );
}

/**
 * The scope selector: Account, or a searchable Family / SKU picker. The search
 * queries the server (so it reaches beyond any first-page cap), and the currently
 * selected key's label is fetched on its own so a selection outside the current
 * search results still shows its name.
 */
function ScopePicker({
  scope,
  selectedKey,
  lockScope,
  onScope,
  onKey,
}: {
  scope: ChartState['scope'];
  selectedKey: string | null;
  lockScope: boolean;
  onScope: (scope: ChartState['scope']) => void;
  onKey: (key: string) => void;
}) {
  const [search, setSearch] = useState('');
  const q = search.trim();

  const families = useQuery({
    queryKey: ['families', 'picker', q],
    queryFn: () => api.get<FamiliesResult>(`/api/families${queryString({ q })}`),
    enabled: scope === 'family',
  });
  const skus = useQuery({
    queryKey: ['skus', 'picker', q],
    queryFn: () => api.get<SkuListResult>(`/api/skus${queryString({ q })}`),
    enabled: scope === 'sku',
  });

  const options = useMemo(() => {
    if (scope === 'family') {
      return (families.data?.families ?? [])
        .slice(0, 100)
        .map((f) => ({ value: f.key, label: f.name || f.key }));
    }
    if (scope === 'sku') {
      return (skus.data?.skus ?? []).slice(0, 100).map((s) => ({
        value: String(s.id),
        label: `${s.sellerSku}${s.title ? ` — ${s.title}` : ''}`,
      }));
    }
    return [];
  }, [scope, families.data, skus.data]);

  // The selected key may not be in the current (searched) result set; fetch its
  // own label so the dropdown still shows it rather than a bare id.
  const inOptions = selectedKey !== null && options.some((o) => o.value === selectedKey);
  const familyLabel = useQuery({
    queryKey: ['family', selectedKey],
    queryFn: () =>
      api.get<FamilyDetailResult>(`/api/family?key=${encodeURIComponent(selectedKey!)}`),
    enabled: scope === 'family' && !!selectedKey && !inOptions,
    retry: false,
  });
  const skuLabel = useQuery({
    queryKey: ['sku', selectedKey],
    queryFn: () => api.get<SkuDetail>(`/api/skus/${selectedKey!}`),
    enabled: scope === 'sku' && !!selectedKey && !inOptions,
    retry: false,
  });

  const selectedLabel =
    scope === 'family'
      ? (familyLabel.data?.family.name ?? familyLabel.data?.family.key)
      : skuLabel.data
        ? `${skuLabel.data.sku.sellerSku}${skuLabel.data.sku.title ? ` — ${skuLabel.data.sku.title}` : ''}`
        : undefined;

  return (
    <>
      <Field label="Scope">
        <Select
          value={scope}
          onChange={(e) => onScope(e.target.value as ChartState['scope'])}
          aria-label="Scope"
          className="w-32"
          disabled={lockScope}
        >
          {!lockScope && <option value="account">Account</option>}
          <option value="family">Family</option>
          {!lockScope && <option value="sku">SKU</option>}
          {/* A product (child ASIN) arrives from the product page; it has no picker. */}
          {scope === 'asin' && <option value="asin">Product</option>}
        </Select>
      </Field>
      {scope === 'asin' && (
        <Field label="Product">
          <span className="flex h-9 items-center gap-2 text-sm">
            <span className="font-mono text-xs text-body">{selectedKey ?? '—'}</span>
            {selectedKey && (
              <Link
                to={productPagePath({ asin: selectedKey, skuId: 0 })}
                className="text-xs text-brand-600 hover:underline"
              >
                Open product
              </Link>
            )}
          </span>
        </Field>
      )}
      {scope !== 'account' && scope !== 'asin' && (
        <>
          <Field label="Search">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={scope === 'family' ? 'Search families' : 'Search SKUs'}
              aria-label={scope === 'family' ? 'Search families' : 'Search SKUs'}
              className="w-48"
            />
          </Field>
          <Field label={scope === 'family' ? 'Family' : 'SKU'}>
            <Select
              value={selectedKey ?? ''}
              onChange={(e) => onKey(e.target.value)}
              aria-label={scope === 'family' ? 'Family' : 'SKU'}
              className="w-64"
            >
              <option value="">Choose…</option>
              {selectedKey !== null && !inOptions && (
                <option value={selectedKey}>{selectedLabel ?? selectedKey}</option>
              )}
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </>
      )}
    </>
  );
}
